import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FilterMenuSinglePanel } from 'apps/sigma2-ui/src/filters-v2/components/FilterMenuSinglePanel';
import { FilterListBoxItem } from 'apps/sigma2-ui/src/filters-v2/components/FilterListBox';
import { ExternalLinkSvg } from 'apps/sigma2-ui/src/components/svg/ExternalLinkSvg';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';
import { DashboardRecordDto } from 'libs/end-to-end/src/dashboard';
import { getNextChartPosition } from 'libs/end-to-end/src/dashboard';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { SvgProps } from 'apps/sigma2-ui/src/components/svg/types';
import { useSearchParams } from 'react-router-dom';
import { getNavigationUrlWhileKeepingExistingPermalinks } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { Phrases } from 'libs/end-to-end/src/phrases';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { DashboardsSearchableFilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/DashboardsSearchableFilterListBox';
import { getTranslatedContentAsString } from 'libs/end-to-end/src/translation-content';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { ViewConfigurationSchema } from 'libs/end-to-end/src/view-configurations';

const NavigateToDashboardButton = ({
  dashboardId,
  svgProps,
}: {
  dashboardId: string;
  svgProps: SvgProps;
}) => {
  const [searchParams] = useSearchParams();

  const handleNavigateToDashboard = useCallback(() => {
    window.open(
      getNavigationUrlWhileKeepingExistingPermalinks({
        navigateToUrl: `/dashboards/${dashboardId}`,
        existingSearchParams: searchParams,
      }),
      '_blank',
      'noreferrer',
    );
  }, [dashboardId, searchParams]);

  return (
    <Button
      variant="outline"
      innerClassName={['border-0 !py-0']}
      size="small"
      role="button"
      behavior={{ type: 'onClick', onClick: handleNavigateToDashboard }}
    >
      <ExternalLinkSvg {...svgProps} />
    </Button>
  );
};

const createDashboardItem = ({
  dashboard,
  isSelected,
  language,
}: {
  dashboard: DashboardRecordDto;
  isSelected: boolean;
  language: Phrases;
}): FilterListBoxItem => ({
  value: dashboard.id,
  translatedContent: language.dangerousUserInput({
    value: dashboard.name,
  }),
  selected: isSelected,
  icon: isSelected
    ? (svgProps: SvgProps) => (
        <NavigateToDashboardButton
          dashboardId={dashboard.id}
          svgProps={svgProps}
        />
      )
    : null,
  color: isSelected ? 'royal-700' : 'gray-900',
});

export const AddToDashboardMenu = ({
  getCustomVizRequest,
  closeMenu,
}: {
  getCustomVizRequest: () => {
    customVizRequest: CustomVizRequest | undefined;
    viewConfiguration?: ViewConfigurationSchema;
  };
  closeMenu: () => void;
}) => {
  const language = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const { myObservation } = useObservation();

  const { dashboardsList, addChartToDashboard } = useDashboards();
  const [selectedDashboardId, setSelectedDashboardId] = useState<string | null>(
    null,
  );

  const selectedDashboard = useMemo(
    () => dashboardsList?.find((d) => d.id === selectedDashboardId) ?? null,
    [dashboardsList, selectedDashboardId],
  );

  // eslint-disable-next-line complexity
  const handleAddChartToDashboard = useCallback(async () => {
    if (!selectedDashboard) {
      return;
    }
    setIsLoading(true);

    const { customVizRequest, viewConfiguration = {} } = getCustomVizRequest();

    const { defaultViewMode, selectedMetric, baseColors } = viewConfiguration;

    if (!customVizRequest || !myObservation) {
      setIsLoading(false);
      return;
    }

    if (
      viewConfiguration &&
      customVizRequest.customVizId === 'tableView' &&
      customVizRequest.inputs.extras.visibleDepartments
    ) {
      viewConfiguration.visibleDepartments =
        customVizRequest.inputs.extras.visibleDepartments.map((v) =>
          parseInt(v),
        );
    }

    await addChartToDashboard({
      targetDashboardId: selectedDashboard.id,
      chart: {
        customVizRequest,
        defaultViewMode: defaultViewMode ?? 'table',
        selectedMetric: selectedMetric ?? null,
        baseColors: baseColors ?? null,
        viewConfiguration,
        name: getTranslatedContentAsString(
          language.savedAnalysisCardTitle({
            myObservation,
            request: customVizRequest,
          }),
        ),
        dashboardId: selectedDashboard.id,
        description: null,
        width: 100,
        height: null,
        ...getNextChartPosition(selectedDashboard.charts),
      },
    });

    setIsLoading(false);
    closeMenu();
  }, [
    addChartToDashboard,
    closeMenu,
    getCustomVizRequest,
    language,
    myObservation,
    selectedDashboard,
  ]);

  const items = useMemo<FilterListBoxItem[]>(() => {
    return (
      dashboardsList?.map((dashboard) =>
        createDashboardItem({
          dashboard,
          isSelected: dashboard.id === selectedDashboardId,
          language,
        }),
      ) ?? []
    );
  }, [dashboardsList, selectedDashboardId, language]);

  useEffect(() => {
    if (
      selectedDashboardId &&
      dashboardsList &&
      !dashboardsList.some((d) => d.id === selectedDashboardId)
    ) {
      setSelectedDashboardId(null);
    }
  }, [dashboardsList, selectedDashboardId]);

  return (
    <FilterMenuSinglePanel
      filterContent={
        dashboardsList === null ? (
          <Spinner />
        ) : (
          <DashboardsSearchableFilterListBox
            multiple={false}
            items={items}
            onChange={(newValues) =>
              setSelectedDashboardId(newValues[0] ?? null)
            }
          />
        )
      }
      bottomProps={{
        onApply: handleAddChartToDashboard,
        onCancel: closeMenu,
        applyDisabled: selectedDashboardId === null || isLoading,
        errorTranslatedContent: null,
      }}
    />
  );
};
